(() => {
  if (window.__bsEditorialHomeInitialized) return;
  window.__bsEditorialHomeInitialized = true;

  const readyHomes = () => {
    window.requestAnimationFrame(() => {
      document.querySelectorAll('.bs-home').forEach((home) => {
        home.classList.add('is-ready');
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', readyHomes, { once: true });
  } else {
    readyHomes();
  }

  document.addEventListener('shopify:section:load', readyHomes);

  // Time-based reveal, triggered by first visibility. Confirmed via direct console
  // measurement on the live site that a *scroll-linked* version (--bs-reveal-p driven 1:1 by
  // scroll position, matching the hero's technique) was working exactly as coded — the value
  // climbed smoothly from 0 to 1 in perfect sync with scroll — but a fast scroll gesture
  // (trackpad flick, several mouse-wheel clicks) covers that whole distance before the user
  // stops scrolling to actually look, so by the time they look it's already at rest. A
  // *time-based* transition doesn't have that failure mode: once triggered by
  // IntersectionObserver (fires the instant any part of the row is visible), it plays out
  // over a fixed 0.6s of real time no matter how fast or slow the scroll was — including
  // continuing to animate for a moment *after* the user has already stopped scrolling and is
  // just looking at it, which is the opposite of the scroll-linked version's failure mode.
  const prefersReducedMotionForReveal = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!prefersReducedMotionForReveal && 'IntersectionObserver' in window) {
    const registerReveal = (shellSelector, itemSelector) => {
      document.querySelectorAll(shellSelector).forEach((shell) => {
        const items = shell.querySelectorAll(itemSelector);
        if (!items.length) return;
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            shell.classList.add('is-revealed');
            observer.unobserve(entry.target);
          });
        }, { threshold: 0 });
        observer.observe(shell);
      });
    };
    registerReveal('.bs-story-shell', '.bs-story__card');
    registerReveal('.bs-scene-shell', '.bs-scene-nav__item');
    registerReveal('.bs-brand-shell', '.bs-brand-card');
  }

  const setupSlider = (slider, options) => {
    slider.querySelectorAll('.bs-story__card--clone').forEach((clone) => clone.remove());
    delete slider.dataset.bsPeekCloned;

    const cards = Array.from(slider.children);
    if (cards.length < 2) return;

    // Some rails (observed on the brand rail specifically) end up pre-scrolled by roughly
    // their own left padding on first paint — likely the browser settling scroll position as
    // lazy-loaded card images resolve their real dimensions. A single reset wasn't enough, so
    // this pins scrollLeft back to 0 repeatedly across the points where that drift showed up:
    // immediately, next frame, and once every asset (fonts/images) has actually finished
    // loading — after which it's safe to stop forcing it, so a real user scroll isn't fought.
    let userInteracted = false;
    const pinToStart = () => { if (!userInteracted) slider.scrollLeft = 0; };
    pinToStart();
    requestAnimationFrame(pinToStart);
    if (document.readyState === 'complete') {
      pinToStart();
    } else {
      window.addEventListener('load', pinToStart, { once: true });
    }

    let index = 0;
    const shell = slider.closest(options.shellSelector);
    const prev = shell?.querySelector(options.prevSelector);
    const next = shell?.querySelector(options.nextSelector);
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let scrollFrame = null;
    const originalSnapType = slider.style.scrollSnapType;
    slider.style.scrollBehavior = 'auto';

    const updateControls = () => {
      const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
      const isScrollable = maxScroll > 2;
      shell?.classList.toggle('is-scrollable', isScrollable);
      if (prev) prev.disabled = !isScrollable || slider.scrollLeft <= 2;
      if (next) next.disabled = !isScrollable || slider.scrollLeft >= maxScroll - 2;
    };

    const getCurrentIndex = () => {
      const scrollLeft = slider.scrollLeft + slider.offsetLeft;
      return cards.reduce((nearestIndex, card, cardIndex) => {
        const currentDistance = Math.abs(cards[nearestIndex].offsetLeft - scrollLeft);
        const nextDistance = Math.abs(card.offsetLeft - scrollLeft);
        return nextDistance < currentDistance ? cardIndex : nearestIndex;
      }, 0);
    };

    const glideTo = (targetLeft) => {
      const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
      const destination = Math.max(0, Math.min(targetLeft, maxScroll));
      if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
      slider.style.scrollSnapType = 'none';
      if (prefersReduced) {
        slider.scrollLeft = destination;
        slider.style.scrollSnapType = originalSnapType;
        updateControls();
        return;
      }
      const startLeft = slider.scrollLeft;
      const distance = destination - startLeft;
      const duration = Math.min(760, Math.max(420, Math.abs(distance) * 1.15));
      const startTime = window.performance.now();
      const ease = (t) => 1 - Math.pow(1 - t, 4);
      const step = (now) => {
        const progress = Math.min(1, (now - startTime) / duration);
        slider.scrollLeft = startLeft + distance * ease(progress);
        if (progress < 1) {
          scrollFrame = window.requestAnimationFrame(step);
        } else {
          scrollFrame = null;
          slider.style.scrollSnapType = originalSnapType;
          updateControls();
        }
      };
      scrollFrame = window.requestAnimationFrame(step);
    };

    const scrollToIndex = (nextIndex) => {
      userInteracted = true;
      index = Math.max(0, Math.min(cards.length - 1, nextIndex));
      glideTo(cards[index].offsetLeft - slider.offsetLeft);
    };

    prev?.addEventListener('click', () => scrollToIndex(index - 1));
    next?.addEventListener('click', () => scrollToIndex(index + 1));
    slider.addEventListener('pointerdown', () => { userInteracted = true; });
    slider.addEventListener('scroll', () => {
      updateControls();
      if (!scrollFrame) index = getCurrentIndex();
    }, { passive: true });
    updateControls();
    window.addEventListener('resize', updateControls);

    if (!options.autoplayMobile || prefersReduced) return;

    window.setInterval(() => {
      if (window.innerWidth >= 990) return;
      scrollToIndex(index + 1);
    }, 3200);
  };

  document.querySelectorAll('[data-bs-story-slider]').forEach((slider) => {
    setupSlider(slider, {
      shellSelector: '.bs-story-shell',
      prevSelector: '[data-bs-story-prev]',
      nextSelector: '[data-bs-story-next]',
      autoplayMobile: true,
      peekDesktop: true,
    });
  });

  document.querySelectorAll('[data-bs-scene-slider]').forEach((slider) => {
    setupSlider(slider, {
      shellSelector: '.bs-scene-shell',
      prevSelector: '[data-bs-scene-prev]',
      nextSelector: '[data-bs-scene-next]',
      autoplayMobile: false,
    });
  });

  document.querySelectorAll('[data-bs-brand-slider]').forEach((slider) => {
    setupSlider(slider, {
      shellSelector: '.bs-brand-shell',
      prevSelector: '[data-bs-brand-prev]',
      nextSelector: '[data-bs-brand-next]',
      autoplayMobile: false,
    });
  });

  const initializedProductSliders = new WeakSet();
  const initProductSliders = (root = document) => {
    const sliders = root.matches?.('[data-bs-product-slider]')
      ? [root]
      : root.querySelectorAll('[data-bs-product-slider]');
    sliders.forEach((slider) => {
      if (initializedProductSliders.has(slider) || slider.children.length < 2) return;
      initializedProductSliders.add(slider);
      setupSlider(slider, {
        shellSelector: '.bs-product-shell',
        prevSelector: '[data-bs-product-prev]',
        nextSelector: '[data-bs-product-next]',
        autoplayMobile: false,
      });
    });
  };
  initProductSliders();
  document.addEventListener('shopify:section:load', (event) => initProductSliders(event.target));

  document.querySelectorAll('[data-bs-journal-slider]').forEach((slider) => {
    setupSlider(slider, {
      shellSelector: '.bs-journal-shell',
      prevSelector: '[data-bs-journal-prev]',
      nextSelector: '[data-bs-journal-next]',
      autoplayMobile: false,
    });
  });

  const positionScenePop = (item) => {
    const pop = item.querySelector('.bs-scene-nav__pop');
    const button = item.querySelector('.bs-scene-nav__button');
    if (!pop || !button) return;

    const rect = button.getBoundingClientRect();
    const popWidth = Math.min(275, window.innerWidth - 36);
    const left = Math.max(18, Math.min(rect.left, window.innerWidth - popWidth - 18));
    pop.style.setProperty('--bs-pop-left', `${left}px`);
    pop.style.setProperty('--bs-pop-top', `${rect.bottom + 10}px`);
    pop.style.setProperty('--bs-pop-width', `${popWidth}px`);
  };

  document.querySelectorAll('.bs-scene-nav__item').forEach((item) => {
    item.addEventListener('mouseenter', () => positionScenePop(item));
    item.addEventListener('focusin', () => positionScenePop(item));
  });

  const isMobileSceneNav = () => window.matchMedia('(max-width: 989px)').matches;
  const sceneCloseTimers = new WeakMap();

  const cancelSceneClose = (item) => {
    const timer = sceneCloseTimers.get(item);
    if (timer) window.clearTimeout(timer);
    sceneCloseTimers.delete(item);
  };

  const closeSceneItem = (item) => {
    cancelSceneClose(item);
    item.classList.remove('is-open');
  };

  const scheduleSceneClose = (item, delay = 520) => {
    cancelSceneClose(item);
    const timer = window.setTimeout(() => {
      sceneCloseTimers.delete(item);
      item.classList.remove('is-open');
    }, delay);
    sceneCloseTimers.set(item, timer);
  };

  const openSceneItem = (item) => {
    document.querySelectorAll('.bs-scene-nav__item.is-open').forEach((openItem) => {
      if (openItem !== item) closeSceneItem(openItem);
    });
    positionScenePop(item);
    item.classList.add('is-open');
    cancelSceneClose(item);
  };

  document.querySelectorAll('.bs-scene-nav__item').forEach((item) => {
    const button = item.querySelector('.bs-scene-nav__button');
    const pop = item.querySelector('.bs-scene-nav__pop');
    if (!button || !pop) return;

    item.addEventListener('mouseenter', () => {
      if (!isMobileSceneNav()) openSceneItem(item);
    });
    item.addEventListener('mouseleave', () => {
      if (!isMobileSceneNav()) scheduleSceneClose(item);
    });
    pop.addEventListener('mouseenter', () => cancelSceneClose(item));
    pop.addEventListener('mouseleave', () => {
      if (!isMobileSceneNav()) scheduleSceneClose(item);
    });
    item.addEventListener('focusin', () => openSceneItem(item));
    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget)) scheduleSceneClose(item);
    });

    button.addEventListener('click', (event) => {
      if (!isMobileSceneNav()) return;
      event.preventDefault();
      const willOpen = !item.classList.contains('is-open');
      document.querySelectorAll('.bs-scene-nav__item.is-open').forEach((openItem) => {
        closeSceneItem(openItem);
      });
      if (willOpen) openSceneItem(item);
    });
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('.bs-scene-nav__item')) return;
    document.querySelectorAll('.bs-scene-nav__item.is-open').forEach((item) => {
      closeSceneItem(item);
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.bs-scene-nav__item.is-open').forEach((item) => {
      closeSceneItem(item);
    });
  });
})();
